import React, { useEffect, useState, useCallback } from "react";
import { cloneDeep, isEmpty } from "lodash";
import { useTranslation } from "react-i18next";
import TagItem from "../PatientTags/TagItem";
import useWorkspaceTagSettingsService from "../CustomTagsManagement/services/workspaceTagSettingsService";
import AwesomeIcon from "../AwesomeIcon";
import { faPlus, faCalendar } from "@fortawesome/pro-regular-svg-icons";
import SectionedDropdownMenu from "../common/SectionedDropdownMenu";

const CustomFieldsFilters = ({
  filtersData,
  setFiltersData,
  selectedWorkspaceId,
  showFilters,
}) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [filterTags, setFilterTags] = useState({});
  const [editingField, setEditingField] = useState(null);
  const [tempEditedValue, setTempEditedValue] = useState(null);

  const { data: customFieldsList } = useWorkspaceTagSettingsService(selectedWorkspaceId);
  const customFields = cloneDeep(customFieldsList)?.filter((field) => {
    // Exclude fields already in custom_tags
    if (filtersData?.custom_tags?.some((_field) => _field.tag_id == field.id)) {
      return false;
    }
    // Exclude special tags: program_type, ai_recommendation, manual_review, program_status, review
    // These are handled separately in the main filter UI
    const identifier = field.additional?.identifier;
    if (
      identifier === "program_type" ||
      identifier === "ai_recommendation" ||
      identifier === "manual_review" ||
      identifier === "program_status" ||
      identifier === "review"
    ) {
      return false;
    }
    return true;
  });

  // Helper function to remove gt/lt entries for a specific field
  const removeGtLtEntries = useCallback((customFields, fieldId) => {
    return customFields?.filter(
      (field) =>
        !(
          field.tag_id === fieldId &&
          (field.logic === "gt" ||
            field.logic === "gte" ||
            field.logic === "lt" ||
            field.logic === "lte")
        )
    );
  }, []);

  // Filter availableCustomFields based on search term
  const filteredCustomFields = customFields?.filter((field) =>
    field.tag.toLowerCase().includes(search.toLowerCase())
  );

  // Group filtered custom fields by section
  const groupedCustomFields = filteredCustomFields?.reduce((acc, field) => {
    const section = field.properties?.section || "Other";
    if (!acc[section]) {
      acc[section] = [];
    }
    acc[section].push(field);
    return acc;
  }, {});

  // Convert grouped fields to sections format for SectionedDropdownMenu
  const sections = groupedCustomFields
    ? Object.entries(groupedCustomFields).map(([sectionName, fields]) => ({
        title: sectionName.toUpperCase(),
        items:
          fields?.map((field) => ({
            name: field.tag,
            onClick: () => handleAddTag(field),
            className: "p-[8px] text-[12px]",
            icon:
              field.tag_type === "date" ? (
                <AwesomeIcon name={faCalendar} className="w-4 h-4" />
              ) : undefined,
          })) || [],
      }))
    : [];

  // Reset refs when filtersData.custom_tags is empty
  useEffect(() => {
    if (!isEmpty(filtersData.custom_tags) && Array.isArray(filtersData.custom_tags)) {
      const _filterTags = {};

      // Exclude ai_recommendation, manual_review, program_type, program_status, review — they are shown in the separate UI above in SearchFilters
      const specialIdentifiers = [
        "program_type",
        "ai_recommendation",
        "manual_review",
        "program_status",
        "review",
      ];
      const isSpecialTag = (tagId) => {
        const meta = customFieldsList?.find((f) => f.id == tagId);
        return specialIdentifiers.includes(meta?.additional?.identifier);
      };

      // Group fields by tag_id to handle "between" logic
      const groupedFields = {};
      filtersData.custom_tags.forEach((field) => {
        if (isSpecialTag(field.tag_id)) return;
        if (!groupedFields[field.tag_id]) {
          groupedFields[field.tag_id] = [];
        }
        groupedFields[field.tag_id].push(field);
      });

      // Process each group
      Object.entries(groupedFields).forEach(([tagId, fields]) => {
        const newField = cloneDeep(customFieldsList)?.find(
          (_field) => _field.id == tagId
        );

        if (!newField) return;

        // Check if this is a "between" case (has both gt and lt logic)
        const gtField = fields.find((f) => f.logic === "gte");
        const ltField = fields.find((f) => f.logic === "lte");

        if (gtField && ltField && newField.tag_type === "date") {
          // This is a "between" case
          _filterTags[tagId] = {
            ...newField,
            value: [gtField.value, ltField.value], // Array of [startDate, endDate]
            logic: "between",
          };
        } else {
          // This is a regular case, use the first field with comparison logic or fallback to the first field
          const regularField =
            fields.find(
              (f) =>
                f.logic === "gt" ||
                f.logic === "gte" ||
                f.logic === "lt" ||
                f.logic === "lte" ||
                f.logic === "exact" ||
                f.logic === "contains"
            ) || fields[0];
          _filterTags[tagId] = {
            ...newField,
            value: regularField.value,
            logic: regularField.logic || "exact",
          };
        }
      });

      setFilterTags(_filterTags);
    } else {
      setFilterTags({});
      setEditingField(null);
      setTempEditedValue(null);
    }
  }, [filtersData.custom_tags, customFieldsList]);

  useEffect(() => {
    if (!showFilters) {
      setFilterTags((prev) => {
        const filtered = {};
        Object.entries(prev).forEach(([key, tag]) => {
          // For numeric fields, allow 0 as a valid value
          if (tag.tag_type === "integer" || tag.tag_type === "float") {
            if (tag.value !== undefined && tag.value !== null && tag.value !== "") {
              filtered[key] = tag;
            }
          } else if (
            tag.value !== undefined &&
            tag.value !== null &&
            !(typeof tag.value === "string" && tag.value.trim() === "") &&
            !(Array.isArray(tag.value) && tag.value.length === 0)
          ) {
            filtered[key] = tag;
          }
        });
        return filtered;
      });
      setEditingField(null);
      setTempEditedValue(null);
    }
  }, [showFilters]);

  const onChangeField = (field) => {
    const _filtersData = { ...filtersData };
    const fieldId = field.id;
    const tagType = field.tag_type;
    const _customFields = [...(_filtersData?.custom_tags || [])];

    let logic = field.logic || "exact";
    let _value = field.value;
    if (tagType === "integer") {
      _value =
        _value !== null && _value !== undefined && _value !== ""
          ? parseInt(_value, 10)
          : null;
    } else if (tagType === "float") {
      _value =
        _value !== null && _value !== undefined && _value !== ""
          ? parseFloat(_value)
          : null;
    } else if (tagType === "string") {
      logic = "icontains";
    } else if (tagType === "multi_dropdown") {
      // Handle both array and string values (for backward compatibility with saved filters)
      if (Array.isArray(_value)) {
        _value = _value.map((option) => option.trim());
      } else if (typeof _value === "string" && _value !== "") {
        // Convert string to array for multi_dropdown tags
        _value = [_value.trim()];
      }
      // Only set logic to "contains" if not already set (preserve original logic from saved filters)
      if (!field.logic) {
        logic = "contains";
      }
    } else if (tagType === "user") {
      _value = _value;
      logic = "exact";
    } else if (tagType === "boolean") {
      if (_value === "") {
        _value = null;
      } else if (typeof _value === "string") {
        _value = _value === "true";
      } else {
        _value = !!_value;
      }
      logic = "exact";
    } else if (tagType === "date") {
      // Handle both single dates and date arrays (for "between" logic)
      // Match PatientTags: stored value is UTC ISO string; TagItem displays it in local time via date-fns format().
      if (Array.isArray(_value)) {
        // For "between" logic, keep the array as-is, conversion will happen later
        _value = _value;
      } else {
        // For single dates (e.g. "On" / exact logic): store UTC ISO string so API/PatientTags format is consistent.
        // Display is localised in TagItem via format(new Date(value), "MMM dd, yyyy") which uses local timezone.
        if (_value) {
          if (
            typeof _value === "string" &&
            _value.includes("T") &&
            _value.endsWith("Z")
          ) {
            _value = _value; // Already UTC ISO string (e.g. from saved filter), keep as-is
          } else if (typeof _value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(_value)) {
            // YYYY-MM-DD: treat as local midnight on that day, then to UTC
            const [y, m, d] = _value.split("-").map(Number);
            _value = new Date(y, m - 1, d).toISOString();
          } else {
            // Date object (e.g. from picker = local midnight) → UTC ISO string
            _value = new Date(_value).toISOString();
          }
        } else {
          _value = null;
        }
      }

      logic = field.logic || "exact";
    } else if (tagType === "datetime") {
      logic = "eq";
    } else if (tagType === "single_dropdown") {
      // In filters, single_dropdown supports multi-selection so users can filter by
      // multiple values at once (e.g. Gender: M or F). Treat identically to multi_dropdown.
      // Handle both array (new) and string (backward-compat with saved filters) values.
      if (Array.isArray(_value)) {
        _value = _value.map((option) => option.trim());
      } else if (typeof _value === "string" && _value !== "") {
        _value = [_value.trim()];
      }
      // Only set logic to "in" if not already set (preserve logic from saved filters)
      if (!field.logic) {
        logic = "in";
      }
    }

    // Only add to payload if value is not null/undefined/empty
    if (_value != null && _value !== "") {
      // Handle "between" logic for date fields by creating two separate payload objects
      if (
        tagType === "date" &&
        logic === "between" &&
        Array.isArray(_value) &&
        _value.length === 2
      ) {
        // First, clean up any existing entries for this field (both gt/lt and other logic types)
        const filteredFields = _customFields?.filter((field) => field.tag_id !== fieldId);
        _filtersData.custom_tags = filteredFields;

        const [startDate, endDate] = _value;

        // Create payload for start date (greater than)
        if (startDate) {
          const startPayload = {
            tag_id: fieldId,
            value: new Date(startDate).toISOString(),
            logic: "gte",
          };

          const startFieldIndex = filteredFields?.findIndex(
            (field) =>
              field.tag_id === fieldId && (field.logic === "gt" || field.logic === "gte")
          );
          if (startFieldIndex !== -1) {
            filteredFields[startFieldIndex] = startPayload;
          } else {
            filteredFields.push(startPayload);
          }
        }

        // Create payload for end date (less than)
        if (endDate) {
          const endPayload = {
            tag_id: fieldId,
            value: new Date(endDate).toISOString(),
            logic: "lte",
          };

          const endFieldIndex = filteredFields?.findIndex(
            (field) =>
              field.tag_id === fieldId && (field.logic === "lt" || field.logic === "lte")
          );
          if (endFieldIndex !== -1) {
            filteredFields[endFieldIndex] = endPayload;
          } else {
            filteredFields.push(endPayload);
          }
        }
      } else {
        // Handle all other cases with single payload
        // First, clean up any existing "gt" and "lt" entries for this field (from previous "between" logic)
        const filteredFields = removeGtLtEntries(_customFields, fieldId);
        _filtersData.custom_tags = filteredFields;

        const payload = {
          tag_id: fieldId,
          value: _value,
          logic: logic,
        };

        const _customFieldIndex = filteredFields?.findIndex(
          (field) => field.tag_id === fieldId
        );
        if (_customFieldIndex !== -1) {
          filteredFields[_customFieldIndex] = payload;
        } else {
          filteredFields.push(payload);
        }
      }
    } else {
      // Remove the field if value is null/undefined/empty
      // For "between" logic, remove both gt and lt entries
      if (tagType === "date" && logic === "between") {
        const filteredFields = removeGtLtEntries(_customFields, fieldId);
        _filtersData.custom_tags = filteredFields;
      } else {
        const _customFieldIndex = _customFields?.findIndex(
          (field) => field.tag_id === fieldId
        );
        if (_customFieldIndex !== -1) {
          _customFields?.splice(_customFieldIndex, 1);
        }
      }
    }
    // _filtersData.custom_tags = _customFields;
    setFiltersData(_filtersData);
  };

  const handleSearch = (value) => {
    const newValue = typeof value === "function" ? value(search) : value;
    setSearch(newValue);
  };

  const handleKeyDown = (e) => {
    if (e.key === " ") {
      e.stopPropagation();
    }
  };

  const renderEmptyState = () => {
    return (
      <div className="px-4 py-2 text-gray-500 dark:text-gray-400 text-sm">
        {t("no_available_fields")}
      </div>
    );
  };

  const handleAddTag = useCallback(
    (field) => {
      if (!field.id) {
        console.error("Field is missing required property (id)");
        return;
      }
      setFilterTags((prev) => ({
        ...prev,
        [field.id]: { ...field },
      }));

      setEditingField(field.id);
      setTempEditedValue("");
    },
    [filteredCustomFields, filterTags]
  );

  const handleEdit = useCallback((field) => {
    // No isEditable check here — in CustomFieldsFilters the purpose is filtering,
    // so users must always be able to re-edit a filter value regardless of field permissions.
    setEditingField(field.id);
    // For numeric fields, allow 0 as a valid value
    if (field.tag_type === "integer" || field.tag_type === "float") {
      setTempEditedValue(
        filterTags[field.id]?.value !== null && filterTags[field.id]?.value !== undefined
          ? filterTags[field.id]?.value
          : ""
      );
    } else {
      setTempEditedValue(filterTags[field.id]?.value || "");
    }
  });

  const handleValueChange = useCallback((value) => {
    setTempEditedValue(value);
  }, []);

  const handleLogicChange = useCallback(
    (logic) => {
      if (editingField) {
        setFilterTags((prev) => ({
          ...prev,
          [editingField]: { ...prev[editingField], logic },
        }));
      }
    },
    [editingField]
  );

  const handleCancel = (field) => {
    setEditingField(null);
    setTempEditedValue(null);
    // For numeric fields, allow 0 as a valid value
    if (field.tag_type === "integer" || field.tag_type === "float") {
      if (field.value === null || field.value === undefined || field.value === "") {
        handleRemove(field);
      }
    } else if (
      field.value === null ||
      field.value === undefined ||
      (typeof field.value === "string" && field.value.trim() === "") ||
      (Array.isArray(field.value) && field.value.length === 0)
    ) {
      handleRemove(field);
    }
  };

  const handleSave = useCallback(
    (field) => {
      if (!field.id) return;
      field.value = tempEditedValue;
      // For numeric fields, allow 0 as a valid value
      if (field.tag_type === "integer" || field.tag_type === "float") {
        if (field.value === null || field.value === undefined || field.value === "") {
          handleRemove(field);
          return;
        }
      } else if (
        field.value === null ||
        field.value === undefined ||
        (typeof field.value === "string" && field.value.trim() === "") ||
        (Array.isArray(field.value) && field.value.length === 0)
      ) {
        handleRemove(field);
        return;
      }
      // Preserve the logic if it exists
      if (filterTags[field.id]?.logic) {
        field.logic = filterTags[field.id].logic;
      }
      setFilterTags((prev) => {
        const _filterTags = { ...prev, [field.id]: field };
        return _filterTags;
      });
      onChangeField(field);
      setEditingField(null);
      setTempEditedValue(null);
    },
    [tempEditedValue, filterTags]
  );

  const handleRemove = (field) => {
    setFilterTags((prev) => {
      const _filterTags = { ...prev };
      delete _filterTags[field.id];
      return _filterTags;
    });
    const _filtersData = { ...filtersData };
    const _customFields = (_filtersData?.custom_tags || []).filter(
      (f) => f.tag_id !== field.id
    );
    _filtersData.custom_tags = _customFields;
    setFiltersData(_filtersData);
    setEditingField(null);
  };

  // Handle empty custom fields after all hooks are called
  if (!customFields?.length && Object.keys(filterTags).length === 0) {
    return null;
  }

  return (
    <div className="space-y-3 px-5">
      <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 text-[12px] font-semibold">
        <div className="relative flex items-center gap-2">
          <div className="uppercase">{t("Custom Fields")}</div>
          <SectionedDropdownMenu
            sections={sections}
            Icon={() => (
              <div className="flex items-center justify-center w-6 h-6 rounded-full bg-teal-500 hover:bg-teal-600 transition-colors">
                <AwesomeIcon name={faPlus} className="w-4 h-4 text-white" />
              </div>
            )}
            isSearch={true}
            search={search}
            setSearch={handleSearch}
            menuItemClassName="!w-56 z-50 origin-top-left -left-20 top-6 afte:left-6 !bg-white dark:!bg-gray-900 !border !border-gray-200 dark:!border-gray-700"
            scrollableContainerClassName="h-48 overflow-y-auto overflow-x-hidden"
            childrenContent={renderEmptyState()}
            onKeyDown={handleKeyDown}
            disabled={!!editingField}
          />
        </div>
      </div>
      <div className="flex items-start gap-3 p-3 min-h-72 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-md">
        <div className="flex flex-wrap items-start gap-3 w-full">
          {Object.values(filterTags).length > 0 ? (
            Object.values(filterTags).map((field) => {
              if (!field) return null;
              return (
                <TagItem
                  key={field.id}
                  field={field}
                  value={editingField === field.id ? tempEditedValue : field.value}
                  isEditing={editingField === field.id}
                  onEdit={() => handleEdit(field)}
                  onSave={() => handleSave(field)}
                  onCancel={() => handleCancel(field)}
                  onValueChange={handleValueChange}
                  onRemove={() => handleRemove(field)}
                  showLogic={true}
                  logic={field.logic}
                  onLogicChange={handleLogicChange}
                  useLegacyDesign={true}
                  useCustomFieldFiltersLayout={true}
                  useSectionColorBorders={false}
                />
              );
            })
          ) : (
            <div className="flex items-center justify-center w-full h-64 text-gray-500 dark:text-gray-400 text-sm">
              {t("no_tags_selected")}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomFieldsFilters;
